import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { ViewModel as LoadData } from 'TypeGen/Order/Load/view-model';
import { Command as LoadRequest } from 'TypeGen/Order/Load/command';
import { RequestFreightDetail } from 'TypeGen/Order/Load/request-freight-detail';
import LoadFreight from './LoadFreight';
import { Loader } from '@progress/kendo-react-indicators';
import { JsonResponse } from 'TypeGen/json-response';
import { useEffect, useState } from 'react';
import { ApiLink } from 'TypeGen/api-link';
import { ApiCommand } from 'TypeGen/api-command';
import { fetchApi } from 'core/fetchApi';
import { parseJSON } from 'date-fns';
import { formatNumber } from '@progress/kendo-intl';
import { formatDateTime } from 'utils/format';
import { useTrack } from '../context';
import { Grid, Typography, Link, Box, Alert } from '@mui/material';
import { Input } from '@progress/kendo-react-inputs';

type Props = {
  Link: ApiLink;
  CloseDialog: () => void;
}

const Load = (props: Props) => {

  const track = useTrack();
  const [data, setData] = useState<LoadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dateTime, setDateTime] = useState<Date | null>(new Date());
  const [reference1, setReference1] = useState('');
  const [reference2, setReference2] = useState('');
  const [reference3, setReference3] = useState('');
  const [reference4, setReference4] = useState('');
  const [freightDetails, setFreightDetails] = useState<RequestFreightDetail[]>([]);

  const load = () => {
    if (!dateTime) {
      alert('Please select a date/time');
      return;
    }
    if (freightDetails.some(x => !x.BillOfLading)) {
      alert('Please enter the Bill of Lading');
      return;
    }

    setSaving(true);
    const link = data!.Links.find(x => x.Name == "Load") as ApiCommand;
    const request = {
      DateTime: dateTime,
      Reference1: reference1,
      Reference2: reference2,
      Reference3: reference3,
      Reference4: reference4,
      UnhookTrailer: false,
      TrailerFreightOnBoard: false,
      NewTrailerNumber: '',
      FreightDetails: freightDetails,
      SecuredFreight: false,
  } as LoadRequest;
    fetchApi<JsonResponse>({ url: link.Link, payload: request, method: link.Method })
      .then((response) => {
        if (response.Success) {
          track?.refresh();
          props.CloseDialog();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to load');
      })
      .finally(() => setSaving(false));
  }

  useEffect(() => {
    fetchApi<LoadData>({ url: props.Link.Link, payload: {}, method: props.Link.Method })
        .then((data) => {
            setData(data);
            setDateTime(parseJSON(data.DateTime));
            setReference1(data.Reference1);
            setReference2(data.Reference2);
            setReference3(data.Reference3);
            setReference4(data.Reference4);
            setFreightDetails(data.FreightDetails.map(x => ({
                OrderFreightDetailID: x.OrderFreightDetailID,
                BillOfLading: x.BillOfLading,
                InBondNumber: x.InBondNumber,
                Pieces: x.LoadedPieces,
                PiecesUnitOfMeasure: x.LoadedPiecesUnitOfMeasure,
                Weight: x.LoadedWeight,
                WeightUnitOfMeasure: x.LoadedWeightUnitOfMeasure,
                Length: x.LoadedLength,
                Width: x.LoadedWidth,
                Height: x.LoadedHeight,
                DimsUnitOfMeasure: x.LoadedDimsUnitOfMeasure,
                FreightValue: x.FreightValue,
            })));
            setLoading(false);
        });
  }, [props.Link]);

  if (loading || !data) {
    return <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image"></div>
      <div className="k-loading-color"></div>
    </div>
  }

  return (
    <Dialog
      title={`Load (${data.Sequence} of ${data.StopCount}) - Pro # ${data.OrderNumber}`}
      onClose={props.CloseDialog}
      className='dialog-w1140'
    >
      {saving && <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Loader type="converging-spinner" />
      </Box>}

      {data.RequireBillOfLadingScan && <Alert severity="warning" sx={{ mb: 1 }}>
        Customer requires scanned Bill of Lading document
      </Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body1">
            {data.LocationName !== null && <>{data.LocationName.toUpperCase()}<br /></>}
            {data.AddressLine1 !== null && <>{data.AddressLine1.toUpperCase()}<br /></>}
            {data.City}, {data.State} {data.ZipCode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body1" gutterBottom>
            Scheduled {data.TypeName}{' '}
            <Link href="#" onClick={() => setDateTime(parseJSON(data.ScheduledDateTime))}>
              {formatDateTime(data.ScheduledDateTime)}
            </Link>
          </Typography>
          {data.ArrivalDateTime && (
            <Typography variant="body1" gutterBottom>
              Arrival{' '}
              <Link href="#" onClick={() => setDateTime(parseJSON(data.ArrivalDateTime!))}>
                {formatDateTime(data.ArrivalDateTime)}
              </Link>
            </Typography>
          )}
          {data.DepartDateTime && (
            <Typography variant="body1" gutterBottom>
              Departure{' '}
              <Link href="#" onClick={() => setDateTime(parseJSON(data.DepartDateTime!))}>
                {formatDateTime(data.DepartDateTime)}
              </Link>
            </Typography>
          )}
          {data.Miles > 0 && (
            <Typography variant="body1">
              {formatNumber(data.Miles, 'n0')} Stop Miles
            </Typography>
          )}
        </Grid>
      </Grid>

      <DateTimePicker
        required
        label='Loaded Date/Time'
        format="MM/dd/yyyy HH:mm"
        formatPlaceholder="formatPattern"
        value={dateTime}
        onChange={(e) => setDateTime(e.value)}
        width="100%"
      />

      {data.FreightDetails.map((freight, index) => {
        return <LoadFreight
          piecesUnitOfMeasure={data.PiecesUnitOfMeasures}
          weightUnitOfMeasure={data.WeightUnitOfMeasures}
          dimsUnitOfMeasure={data.DimsUnitOfMeasures}
          data={freight}
          request={freightDetails[index]}
          updateRequest={(freight) => {
            setFreightDetails(freightDetails.map((x, i) => i === index ? { ...x, ...freight } : x));
          }}
        />;
      })}

      <Input
        disabled
        label='Reference No. 1'
        value={reference1}
        onChange={(e) => setReference1(e.value)}
        style={{ width: "100%" }}
      />

      <Input
        disabled
        label='Reference No. 2'
        value={reference2}
        onChange={(e) => setReference2(e.value)}
        style={{ width: "100%" }}
      />

      <Input
        disabled
        label='Reference No. 3'
        value={reference3}
        onChange={(e) => setReference3(e.value)}
        style={{ width: "100%" }}
      />

      <Input
        disabled
        label="Reference No. 4"
        value={reference4}
        onChange={(e) => setReference4(e.value)}
        style={{ width: "100%" }}
      />

      <DialogActionsBar layout="end">
        <Button onClick={props.CloseDialog} disabled={saving}>Cancel</Button>
        <Button themeColor="primary" onClick={load} disabled={saving}>Load</Button>
      </DialogActionsBar>
    </Dialog>
  );
}

export default Load;
